import React, {
  useCallback, useEffect, useRef, useState
} from 'react';
import ReactModal from 'react-modal';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';

import Button from '../Button/Button';
import ButtonTypes from '../../constants/buttonTypes';
import postLogout from '../../thunks/auth/logout';
import routes from '../../router/routes';
import { ReactComponent as IconClose } from '../../assets/icon/ic_cross.svg';

const DENVER_TIME_ZONE = 'America/Denver';
const WORKDAY_START_HOUR = 6;
const WORKDAY_END_HOUR = 17;
const WORKDAY_WARNING_MINUTES = 30;
const LOGOUT_DELAY_SECONDS = 30;
const TIME_CHECK_INTERVAL_MS = 10 * 1000;
const WARNING_DISMISSED_STORAGE_KEY = 'workdayWarningDismissed';

const getDenverTime = () => DateTime.now().setZone(DENVER_TIME_ZONE);
const getMinutesSinceMidnight = (dateTime) => dateTime.hour * 60 + dateTime.minute;
const getWarningStartMinutes = () => WORKDAY_END_HOUR * 60 - WORKDAY_WARNING_MINUTES;
const getWorkdayStartMinutes = () => WORKDAY_START_HOUR * 60;
const getWorkdayEndMinutes = () => WORKDAY_END_HOUR * 60;

const isWorkdayOver = (denverTime = getDenverTime()) => {
  const currentMinutes = getMinutesSinceMidnight(denverTime);
  return currentMinutes >= getWorkdayEndMinutes() || currentMinutes < getWorkdayStartMinutes();
};

const isWarningTime = (denverTime = getDenverTime()) => {
  const currentMinutes = getMinutesSinceMidnight(denverTime);
  return currentMinutes >= getWarningStartMinutes() && currentMinutes < getWorkdayEndMinutes();
};

const wasWarningDismissedToday = (denverTime) => (
  sessionStorage.getItem(WARNING_DISMISSED_STORAGE_KEY) === denverTime.toISODate()
);

const EndOfWorkdayModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoggingOut = useRef(false);
  const [visible, setVisible] = useState(isWorkdayOver);
  const [warningVisible, setWarningVisible] = useState(() => {
    const denverTime = getDenverTime();
    return isWarningTime(denverTime) && !wasWarningDismissedToday(denverTime);
  });
  const [logoutDeadline, setLogoutDeadline] = useState(() => (
    isWorkdayOver() ? Date.now() + LOGOUT_DELAY_SECONDS * 1000 : null
  ));
  const [secondsLeft, setSecondsLeft] = useState(LOGOUT_DELAY_SECONDS);

  const showModal = useCallback(() => {
    setWarningVisible(false);
    setLogoutDeadline(Date.now() + LOGOUT_DELAY_SECONDS * 1000);
    setSecondsLeft(LOGOUT_DELAY_SECONDS);
    setVisible(true);
  }, []);

  const logout = useCallback(() => {
    if (isLoggingOut.current) return;
    isLoggingOut.current = true;

    dispatch(postLogout()).finally(() => {
      // The session must end locally even if the logout API is temporarily unavailable.
      localStorage.removeItem('token');
      navigate(routes.LOGIN, { replace: true });
    });
  }, [dispatch, navigate]);

  const closeWarning = useCallback(() => {
    sessionStorage.setItem(WARNING_DISMISSED_STORAGE_KEY, getDenverTime().toISODate());
    setWarningVisible(false);
  }, []);

  useEffect(() => {
    if (visible) return undefined;

    const checkTime = () => {
      const denverTime = getDenverTime();
      const workdayOver = isWorkdayOver(denverTime);

      // Temporary diagnostic log for verifying the Denver time check.
      // eslint-disable-next-line no-console
      console.log('[EndOfWorkdayModal]', {
        denverTime: denverTime.toFormat('yyyy-LL-dd HH:mm:ss ZZZZ'),
        workdayOver
      });

      if (workdayOver) {
        showModal();
      } else if (isWarningTime(denverTime) && !wasWarningDismissedToday(denverTime)) {
        setWarningVisible(true);
      }
    };

    checkTime();
    const intervalId = setInterval(checkTime, TIME_CHECK_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [showModal, visible]);

  useEffect(() => {
    if (!visible || !logoutDeadline) return undefined;

    const updateCountdown = () => {
      const nextSecondsLeft = Math.max(0, Math.ceil((logoutDeadline - Date.now()) / 1000));
      setSecondsLeft(nextSecondsLeft);

      if (nextSecondsLeft === 0) logout();
    };

    updateCountdown();
    const intervalId = setInterval(updateCountdown, 1000);
    return () => clearInterval(intervalId);
  }, [logout, logoutDeadline, visible]);

  ReactModal.setAppElement('#root');

  return (
    <>
      <ReactModal
        isOpen={warningVisible}
        onRequestClose={closeWarning}
        shouldCloseOnOverlayClick={false}
        className="border-none bg-transparent"
        overlayClassName="fixed inset-0 z-[99] flex items-center justify-center bg-[rgba(30,30,45,.65)]"
        contentLabel="Workday ending soon"
      >
        <div className="relative m-[20px] flex min-w-[350px] max-w-[520px] flex-col items-center rounded-[8px]
          bg-white p-[35px] text-center shadow-default dark:bg-background-dark"
        >
          <button
            type="button"
            onClick={closeWarning}
            className="absolute right-[18px] top-[18px] p-[5px] text-text-muted"
            aria-label="Close"
          >
            <IconClose />
          </button>
          <div
            className="mb-[24px] flex h-[70px] w-[70px] items-center justify-center rounded-full
            border-2 border-warning text-[48px] text-warning"
          >
            !
          </div>
          <h2 className="mb-[12px] text-[22px] font-semibold text-text-main dark:text-secondary">
            The workday will end in 30 minutes
          </h2>
          <p className="mb-[28px] text-[15px] text-text-muted">
            Please finish your current work before 5:00 PM.
          </p>
          <Button
            text="OK"
            onClick={closeWarning}
            type={ButtonTypes.WARNING}
            containerStyle="min-w-[120px]"
          />
        </div>
      </ReactModal>

      <ReactModal
        isOpen={visible}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        className="border-none bg-transparent"
        overlayClassName="fixed inset-0 z-[100] flex items-center justify-center bg-[rgba(30,30,45,.65)]"
        contentLabel="Workday is over"
      >
        <div
          className="m-[20px] flex min-w-[350px] max-w-[520px] flex-col items-center rounded-[8px]
          bg-white p-[35px] text-center shadow-default dark:bg-background-dark"
        >
          <div
            className="mb-[24px] flex h-[70px] w-[70px] items-center justify-center rounded-full
            border-2 border-warning text-[48px] text-warning"
          >
            !
          </div>
          <h2 className="mb-[12px] text-[22px] font-semibold text-text-main dark:text-secondary">
            The workday is over. See you tomorrow!
          </h2>
          <p className="mb-[28px] text-[15px] text-text-muted">
            You will be signed out automatically in
            {' '}
            {secondsLeft}
            {' '}
            seconds.
          </p>
          <Button
            text="Sign out now"
            onClick={logout}
            type={ButtonTypes.DANGER}
            containerStyle="min-w-[180px]"
          />
        </div>
      </ReactModal>
    </>
  );
};

export default EndOfWorkdayModal;
